/**
 * @file Utilitaire interne de render/ : assemble des primitives PARTAGEES (GeometryLibrary) en geometries fusionnees a couleurs de sommets.
 *       Les primitives ne sont JAMAIS modifiees : chaque piece est clonee avant applyMatrix4 (puis le clone est dispose).
 *       Reduit les draw calls (un maillage par os / par prototype au lieu d'un par primitive) sans changer le style (contour via ToonKit).
 */
import { BufferAttribute, Color, Euler, Matrix4, Quaternion, Vector3 } from 'three';
import type { BufferGeometry, EulerOrder } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Position / rotation (radians, ordre XYZ) / echelle d'une piece dans le repere de son os. */
export interface PartOptions {
  x?: number;
  y?: number;
  z?: number;
  rx?: number;
  ry?: number;
  rz?: number;
  /** Ordre des rotations d'Euler (defaut 'XYZ' ; 'YXZ' = lacet puis inclinaison locale : feuilles, cornes...). */
  order?: EulerOrder;
  /** Echelle uniforme (multipliee par sx/sy/sz). */
  s?: number;
  sx?: number;
  sy?: number;
  sz?: number;
  /** Faux = la piece est dessinee mais n'entre pas dans la coque de contour (details : joues, reflets...). Defaut vrai. */
  outline?: boolean;
  /** Etiquette de groupe (materiaux differents : 'main', 'glow', 'shimmer'...). Defaut 'main'. */
  group?: string;
}

interface Entry {
  geometry: BufferGeometry;
  matrix: Matrix4;
  color: Color;
  outline: boolean;
  group: string;
}

const scratchEuler = new Euler();
const scratchQuat = new Quaternion();
const scratchPos = new Vector3();
const scratchScale = new Vector3();

/** Matrice TRS depuis des options. */
export const matrixOf = (o: PartOptions = {}): Matrix4 => {
  const s = o.s ?? 1;
  scratchPos.set(o.x ?? 0, o.y ?? 0, o.z ?? 0);
  scratchEuler.set(o.rx ?? 0, o.ry ?? 0, o.rz ?? 0, o.order ?? 'XYZ');
  scratchQuat.setFromEuler(scratchEuler);
  scratchScale.set((o.sx ?? 1) * s, (o.sy ?? 1) * s, (o.sz ?? 1) * s);
  return new Matrix4().compose(scratchPos, scratchQuat, scratchScale);
};

/** Melange de deux couleurs hex (t = 0 -> a, t = 1 -> b), retourne un hex. */
export const mixHex = (a: string, b: string, t: number): string => '#' + new Color(a).lerp(new Color(b), t).getHexString();

export class MergeBuilder {
  private readonly entries: Entry[] = [];

  /** Ajoute une primitive (partagee) teintee `color` (hex, ou Color brut : valeurs ecrites telles quelles dans l'attribut) placee par `o`. */
  add(geometry: BufferGeometry, color: string | Color, o: PartOptions = {}): this {
    this.entries.push({ geometry, matrix: matrixOf(o), color: typeof color === 'string' ? new Color(color) : color.clone(), outline: o.outline ?? true, group: o.group ?? 'main' });
    return this;
  }

  /** Ajoute une geometrie deja placee (matrice identite) : sert a refusionner les ProtoPart en geometrie a couleurs de sommets. */
  addRaw(geometry: BufferGeometry, color: string | Color, group = 'main'): this {
    this.entries.push({ geometry, matrix: new Matrix4(), color: typeof color === 'string' ? new Color(color) : color.clone(), outline: true, group });
    return this;
  }

  get size(): number {
    return this.entries.length;
  }

  groups(): string[] {
    return [...new Set(this.entries.map((e) => e.group))];
  }

  /** Geometrie fusionnee (non indexee, position + normal + color) des pieces du groupe ; null si vide. */
  fill(group = 'main'): BufferGeometry | null {
    return this.merge(this.entries.filter((e) => e.group === group), true);
  }

  /** Geometrie source de la coque de contour : toutes les pieces `outline` (tous groupes sauf `exclude`) ; null si vide. */
  hull(exclude: readonly string[] = []): BufferGeometry | null {
    return this.merge(this.entries.filter((e) => e.outline && !exclude.includes(e.group)), false);
  }

  private merge(list: Entry[], withColor: boolean): BufferGeometry | null {
    if (list.length === 0) return null;
    const prepared: BufferGeometry[] = [];
    for (const e of list) {
      let g = e.geometry.clone();
      g.applyMatrix4(e.matrix);
      for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal') g.deleteAttribute(name);
      if (g.index) {
        const flat = g.toNonIndexed();
        g.dispose();
        g = flat;
      }
      if (withColor) {
        const n = g.getAttribute('position').count;
        const arr = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) {
          arr[i * 3] = e.color.r;
          arr[i * 3 + 1] = e.color.g;
          arr[i * 3 + 2] = e.color.b;
        }
        g.setAttribute('color', new BufferAttribute(arr, 3));
      }
      prepared.push(g);
    }
    const merged = mergeGeometries(prepared, false);
    for (const g of prepared) g.dispose();
    if (!merged) throw new Error('MergeBuilder: attributs incompatibles');
    merged.computeBoundingBox();
    merged.computeBoundingSphere();
    return merged;
  }
}
